/**
 * Web Push helper — service worker registration, permission request,
 * subscription lifecycle. All functions tolerate missing API support
 * (older browsers, iOS Safari before 16.4, etc.) — they return a clear
 * status code so callers can render the appropriate UI.
 */
import { axiosCaller } from '../utils/apiCaller';

const SW_PATH = '/sw.js';

export type SubscribeResult =
    | { status: 'ok' }
    | { status: 'unsupported' }
    | { status: 'permission_denied' }
    | { status: 'permission_default' }
    | { status: 'failed'; error: string };

export function isPushSupported(): boolean {
    return typeof window !== 'undefined'
        && 'serviceWorker' in navigator
        && 'PushManager' in window
        && 'Notification' in window;
}

/**
 * Register the service worker. Idempotent — calling against an already-
 * registered worker just returns the existing registration. Should be called
 * on app boot so push events can be handled even when the user hasn't
 * explicitly clicked "Enable" (they may have subscribed previously).
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if (!isPushSupported()) return null;
    try {
        return await navigator.serviceWorker.register(SW_PATH);
    } catch (e) {
        // Service worker registration can fail on file:// origins, in some
        // private browsing modes, or under aggressive content-blockers. Fall
        // back to nothing — the app still works, just without push.
        // eslint-disable-next-line no-console
        console.warn('[webPush] Service worker registration failed', e);
        return null;
    }
}

export async function getCurrentSubscription(): Promise<PushSubscription | null> {
    if (!isPushSupported()) return null;
    try {
        const reg = await navigator.serviceWorker.ready;
        return await reg.pushManager.getSubscription();
    } catch {
        return null;
    }
}

/**
 * URL-safe base64 (VAPID public key format) → Uint8Array (Push API format).
 * The Push API rejects raw base64 strings — it wants a typed byte buffer.
 */
function urlBase64ToUint8Array(base64: string): Uint8Array {
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(b64);
    const buffer = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) buffer[i] = raw.charCodeAt(i);
    return buffer;
}

/**
 * Best-effort "Chrome on Windows" label from the User-Agent so users can
 * recognize their devices later in Settings ("which Chrome was that?").
 * Capped at 64 chars; the backend caps user_agent at 500.
 */
function deviceLabelFromUA(): string {
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    let browser = 'Browser';
    let os = '';
    if (/Edg\//.test(ua)) browser = 'Edge';
    else if (/Chrome\//.test(ua)) browser = 'Chrome';
    else if (/Firefox\//.test(ua)) browser = 'Firefox';
    else if (/Safari\//.test(ua)) browser = 'Safari';
    if (/Windows/.test(ua)) os = 'Windows';
    else if (/Mac OS X|Macintosh/.test(ua)) os = 'macOS';
    else if (/Android/.test(ua)) os = 'Android';
    else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';
    else if (/Linux/.test(ua)) os = 'Linux';
    return os ? `${browser} on ${os}`.slice(0, 64) : browser;
}

/**
 * Full subscribe flow: register SW → request permission → fetch VAPID key
 * → subscribe with Push API → POST subscription to backend.
 *
 * Idempotent in the happy path: if the user is already subscribed, the
 * existing subscription is re-posted to the backend (which upserts on the
 * endpoint URL, so the row stays valid).
 */
export async function subscribeForPush(): Promise<SubscribeResult> {
    if (!isPushSupported()) return { status: 'unsupported' };

    // Permission. If "default" we trigger the native prompt; if "denied" we
    // bail (browsers don't let JS re-prompt once denied — user must reset it
    // in browser settings).
    if (Notification.permission === 'denied') {
        return { status: 'permission_denied' };
    }
    if (Notification.permission === 'default') {
        const result = await Notification.requestPermission();
        if (result === 'denied') return { status: 'permission_denied' };
        if (result !== 'granted') return { status: 'permission_default' };
    }

    const reg = await registerServiceWorker();
    if (!reg) return { status: 'failed', error: 'sw_registration_failed' };

    // Wait for the worker to be active — `ready` resolves once the SW is
    // installed AND activated, so subsequent pushManager.subscribe is safe.
    const activeReg = await navigator.serviceWorker.ready;

    try {
        const vapidRes: any = await axiosCaller('GET', 'notifications/vapid-public-key');
        const publicKey = vapidRes?.data?.public_key;
        const keyVersion = vapidRes?.data?.key_version || 1;
        if (!publicKey) return { status: 'failed', error: 'no_vapid_key' };

        let subscription = await activeReg.pushManager.getSubscription();
        if (!subscription) {
            subscription = await activeReg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(publicKey),
            });
        }

        const subJson: any = subscription.toJSON();
        const keys = subJson.keys || {};

        await axiosCaller('POST', 'notifications/subscriptions', {
            platform: 'web',
            endpoint: subJson.endpoint,
            public_key: keys.p256dh,
            auth_token: keys.auth,
            vapid_key_version: keyVersion,
            user_agent: (navigator.userAgent || '').slice(0, 500),
            device_label: deviceLabelFromUA(),
        });

        return { status: 'ok' };
    } catch (e: any) {
        return { status: 'failed', error: e?.message || String(e) };
    }
}

/**
 * Tear down the local Push API subscription. Backend cleanup happens via
 * the Settings page's "Remove device" button (with the subscription's
 * server-side id), or automatically when the next push hits a 410.
 */
export async function unsubscribeFromPush(): Promise<boolean> {
    if (!isPushSupported()) return false;
    try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!sub) return true;
        return await sub.unsubscribe();
    } catch {
        return false;
    }
}
